import React, { useState, useEffect, useRef } from 'react';
import { Functions, httpsCallable } from 'firebase/functions';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Organization } from '../AppManager';
import './CreateAppView.css';

interface DeployAppResponse {
  success: boolean;
  appId: string;
  message: string;
  projectId: string;
  caseId: string;
  workflowTriggered: string;
  providedOptionalFields: Record<string, any>;
}

// App interface to match what we need
interface CreatedApp {
  id: string;
  name: string;
  type: string;
  organizationId: string;
  region?: string;
}

interface CreateAppViewProps {
  functions: Functions;
  organizationId: string;
  organizations: Record<string, Organization>;
  onError: (error: string) => void;
  onSuccess: (message: string) => void;
  setLoading: (loading: boolean) => void;
  onBack: () => void;
  onAppCreated: (createdApp?: CreatedApp) => void;
}

const CreateAppView: React.FC<CreateAppViewProps> = ({
  functions,
  organizationId,
  organizations,
  onError,
  onSuccess,
  setLoading,
  onBack,
  onAppCreated
}) => {
  // Step management
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 4;

  // Form data
  const [appName, setAppName] = useState('');
  const [appType, setAppType] = useState<'single-store' | 'single-store-multi-location' | 'multi-store'>('single-store');
  const [selectedTheme, setSelectedTheme] = useState<'modern-light' | 'modern-dark'>('modern-light');
  const [accentColor, setAccentColor] = useState('#2563eb');
  const [appIconUrl, setAppIconUrl] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [appId, setAppId] = useState('');

  // File upload state
  const [appIconFile, setAppIconFile] = useState<File | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [uploadingAppIcon, setUploadingAppIcon] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // File input refs
  const appIconInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Error state for app ID field
  const [appIdError, setAppIdError] = useState('');

  const selectedOrg = organizations[organizationId];
  const deployAppFunc = httpsCallable(functions, 'deployApp');
  const storage = getStorage();

  // Auto-generate app ID when app name changes
  useEffect(() => {
    if (appName) {
      const baseId = appName
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-');
      setAppId(baseId);
    }
  }, [appName]);

  // Clear app ID error when user modifies the app ID
  useEffect(() => {
    if (appIdError && appId) {
      setAppIdError('');
    }
  }, [appId]);

  // File upload utility function
  const uploadFileToStorage = async (file: File, path: string): Promise<string> => {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  // Handle app icon file selection
  const handleAppIconFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      onError('Please select an image file for the app icon.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      onError('App icon file size must be less than 5MB.');
      return;
    }

    setAppIconFile(file);
    setUploadingAppIcon(true);

    try {
      const timestamp = Date.now();
      const path = `app-assets/${organizationId}/icons/${timestamp}-${file.name}`;
      const downloadUrl = await uploadFileToStorage(file, path);
      setAppIconUrl(downloadUrl);
      onSuccess('App icon uploaded successfully!');
    } catch (error) {
      console.error('Error uploading app icon:', error);
      onError('Failed to upload app icon. Please try again.');
      setAppIconFile(null);
    } finally {
      setUploadingAppIcon(false);
    }
  };

  // Handle logo file selection
  const handleLogoFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      onError('Please select an image file for the logo.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      onError('Logo file size must be less than 5MB.');
      return;
    }

    setLogoFile(file);
    setUploadingLogo(true);

    try {
      const timestamp = Date.now();
      const path = `app-assets/${organizationId}/logos/${timestamp}-${file.name}`;
      const downloadUrl = await uploadFileToStorage(file, path);
      setLogoUrl(downloadUrl);
      onSuccess('Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      onError('Failed to upload logo. Please try again.');
      setLogoFile(null);
    } finally {
      setUploadingLogo(false);
    }
  };

  // Clear app icon
  const clearAppIcon = () => {
    setAppIconFile(null);
    setAppIconUrl('');
    if (appIconInputRef.current) {
      appIconInputRef.current.value = '';
    }
  };

  // Clear logo
  const clearLogo = () => {
    setLogoFile(null);
    setLogoUrl('');
    if (logoInputRef.current) {
      logoInputRef.current.value = '';
    }
  };

  // Step validation
  const isStepValid = (step: number) => {
    switch (step) {
      case 1:
        return appName.trim() !== '';
      case 2:
        return accentColor !== '';
      case 3:
        return true; // Optional step
      case 4:
        return appId.trim() !== '';
      default:
        return false;
    }
  };

  // Navigation handlers
  const handleNext = () => {
    if (currentStep < totalSteps && isStepValid(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!isStepValid(4) || !selectedOrg) {
      onError('Please complete all required fields.');
      return;
    }

    // Clear any existing app ID error
    setAppIdError('');
    setIsSubmitting(true);
    setLoading(true);

    try {
      const result = await deployAppFunc({
        appId: appId.trim(),
        appName: appName.trim(),
        organizationId,
        type: appType,
        theme: selectedTheme,
        appIconUrl: appIconUrl.trim() || undefined,
        logoUrl: logoUrl.trim() || undefined,
        themeAccentColor: accentColor
      });

      const responseData = result.data as DeployAppResponse;
      
      if (responseData && responseData.success) {
        onSuccess(`App "${appName}" created successfully! ${responseData.message}`);
        
        // Create app object to pass back
        const createdApp: CreatedApp = {
          id: responseData.appId,
          name: appName.trim(),
          type: appType,
          organizationId,
          region: selectedOrg.region
        };
        
        onAppCreated(createdApp);
      } else {
        // Display error under the app ID field
        const errorMessage = responseData?.message || 'Failed to create app. Please try again.';
        setAppIdError(errorMessage);
        onError(errorMessage);
      }
    } catch (err: any) {
      console.error('Error creating app: ... ', err);
      console.log(err.message.includes('not available'))
      let errorMessage = err.message || 'Failed to create app. Please try again.';

      
      if (err.message && err.message.includes('not available')) {
        errorMessage = 'App ID not available.';
      }
      
      // Display error under the app ID field
      setAppIdError(errorMessage);
      console.log("app id error: ", errorMessage, appIdError)
      onError(errorMessage);
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  // Step content components
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <div className="step-header">
              <h2>Let's start with the basics</h2>
              <p>What would you like to call your app and what type should it be?</p>
            </div>
            
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="app-name">App Name *</label>
                <input
                  id="app-name"
                  type="text"
                  value={appName}
                  onChange={(e) => setAppName(e.target.value)}
                  placeholder="Enter your app name"
                  className="form-input"
                  autoFocus
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="app-type">App Type *</label>
                <div className="app-type-grid">
                  {[
                    { value: 'single-store', label: 'Single Store', desc: 'One location, simple setup' },
                    { value: 'single-store-multi-location', label: 'Multi-Location', desc: 'One brand, multiple locations' },
                    { value: 'multi-store', label: 'Multi-Store', desc: 'Multiple brands and stores' }
                  ].map((type) => (
                    <div
                      key={type.value}
                      className={`app-type-card ${appType === type.value ? 'selected' : ''}`}
                      onClick={() => setAppType(type.value as any)}
                    >
                      <div className="app-type-radio">
                        <div className={`radio-dot ${appType === type.value ? 'active' : ''}`} />
                      </div>
                      <div className="app-type-content">
                        <h4>{type.label}</h4>
                        <p>{type.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <div className="step-header">
              <h2>Choose your app's appearance</h2>
              <p>Select a theme and accent color that matches your brand</p>
            </div>
            
            <div className="form-section">
              <div className="form-group">
                <label>Theme Variant *</label>
                <div className="theme-selector">
                  {[
                    { value: 'modern-light', label: 'Light Theme', icon: '☀️' },
                    { value: 'modern-dark', label: 'Dark Theme', icon: '🌙' }
                  ].map((theme) => (
                    <div
                      key={theme.value}
                      className={`theme-card ${selectedTheme === theme.value ? 'selected' : ''}`}
                      onClick={() => setSelectedTheme(theme.value as any)}
                    >
                      <div className="theme-icon">{theme.icon}</div>
                      <span>{theme.label}</span>
                      <div className={`theme-radio ${selectedTheme === theme.value ? 'active' : ''}`} />
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="accent-color">Primary Accent Color *</label>
                <div className="color-picker-section">
                  {/* <div className="color-preview" style={{ backgroundColor: accentColor }} /> */}
                  <div className="color-inputs">
                    <input
                      type="color"
                      value={accentColor}
                      onChange={(e) => setAccentColor(e.target.value)}
                      className="color-input"
                      // style={{width: "auto !important"}}
                    />
                    <input
                      type="text"
                      value={accentColor}
                      onChange={(e) => setAccentColor(e.target.value)}
                      placeholder="#2563eb"
                      className="color-text"
                      pattern="^#[0-9A-Fa-f]{6}$"
                    />
                  </div>
                </div>
                <div className="color-presets">
                  {['#2563eb', '#7c3aed', '#dc2626', '#059669', '#ea580c', '#0891b2'].map((color) => (
                    <button
                      key={color}
                      type="button"
                      className="color-preset"
                      style={{ backgroundColor: color }}
                      onClick={() => setAccentColor(color)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <div className="step-header">
              <h2>Add your branding</h2>
              <p>Upload your app icon and logo (optional - you can add these later)</p>
            </div>
            
            <div className="form-section">
              <div className="images-section">
                <div className="image-upload-group">
                  <label>App Icon</label>
                  
                  <div className="file-upload-area">
                    <input
                      ref={appIconInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleAppIconFileChange}
                      className="file-input"
                      disabled={uploadingAppIcon}
                    />
                    
                    <div className="upload-controls">
                      <button
                        type="button"
                        className="upload-button"
                        onClick={() => appIconInputRef.current?.click()}
                        disabled={uploadingAppIcon}
                      >
                        {uploadingAppIcon ? (
                          <>
                            <div className="spinner" />
                            Uploading...
                          </>
                        ) : (
                          'Choose File'
                        )}
                      </button>
                      
                      <input
                        type="url"
                        value={appIconUrl}
                        onChange={(e) => setAppIconUrl(e.target.value)}
                        placeholder="Or paste image URL"
                        className="url-input"
                        disabled={uploadingAppIcon}
                      />
                    </div>
                  </div>
                  
                  <small>Square format recommended (512x512px minimum)</small>
                  
                  {(appIconUrl || appIconFile) && (
                    <div className="image-preview-container">
                      <div className="image-preview">
                        <img 
                          src={appIconUrl || (appIconFile ? URL.createObjectURL(appIconFile) : '')} 
                          alt="App icon preview" 
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }} 
                        />
                      </div>
                      <button
                        type="button"
                        className="remove-image"
                        onClick={clearAppIcon}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="image-upload-group">
                  <label>App Logo</label>
                  
                  <div className="file-upload-area">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoFileChange}
                      className="file-input"
                      disabled={uploadingLogo}
                    />
                    
                    <div className="upload-controls">
                      <button
                        type="button"
                        className="upload-button"
                        onClick={() => logoInputRef.current?.click()}
                        disabled={uploadingLogo}
                      >
                        {uploadingLogo ? (
                          <>
                            <div className="spinner" />
                            Uploading...
                          </>
                        ) : (
                          'Choose File'
                        )}
                      </button>
                      
                      <input
                        type="url"
                        value={logoUrl}
                        onChange={(e) => setLogoUrl(e.target.value)}
                        placeholder="Or paste image URL"
                        className="url-input"
                        disabled={uploadingLogo}
                      />
                    </div>
                  </div>
                  
                  <small>Any aspect ratio (transparent background recommended)</small>
                  
                  {(logoUrl || logoFile) && (
                    <div className="image-preview-container">
                      <div className="image-preview">
                        <img 
                          src={logoUrl || (logoFile ? URL.createObjectURL(logoFile) : '')} 
                          alt="App logo preview" 
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }} 
                        />
                      </div>
                      <button
                        type="button"
                        className="remove-image"
                        onClick={clearLogo}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="step-content">
            <div className="step-header">
              <h2>Finalize your app ID</h2>
              <p>This will be your app's unique identifier and URL</p>
            </div>
            
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="app-id">App ID *</label>
                <input
                  id="app-id"
                  type="text"
                  value={appId}
                  onChange={(e) => setAppId(e.target.value)}
                  placeholder="my-awesome-app"
                  className={`form-input ${appIdError ? 'error' : ''}`}
                  pattern="^[a-z0-9-]+$"
                />
                <small>Only lowercase letters, numbers, and hyphens allowed</small>
                {appIdError && (
                  <div className="field-error">
                    {appIdError}
                  </div>
                )}
              </div>
              
              <div className="url-preview">
                <div className="url-preview-label">Your site will be available at:</div>
                <div className="url-preview-url">
                  <span className="url-id">{appId || 'your-app-id'}</span>
                  <span className="url-domain">.dineplatform.com</span>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="create-app-workflow">
      {/* Header */}
      <div className="workflow-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Apps
        </button>
        <div className="org-info">
          <span>{selectedOrg?.orgName}</span>
          <span className="org-region">({selectedOrg?.region})</span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="progress-section">
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${(currentStep / totalSteps) * 100}%` }}
          />
        </div>
        <div className="progress-text">
          Step {currentStep} of {totalSteps}
        </div>
      </div>

      {/* Main content */}
      <div className="workflow-content">
        {renderStepContent()}
      </div>

      {/* Navigation */}
      <div className="workflow-navigation">
        <button
          type="button"
          className="nav-button secondary"
          onClick={handlePrevious}
          disabled={currentStep === 1}
        >
          Previous
        </button>
        
        {currentStep < totalSteps ? (
          <button
            type="button"
            className="nav-button primary"
            onClick={handleNext}
            disabled={!isStepValid(currentStep)}
          >
            Continue
          </button>
        ) : (
          <button
            type="button"
            className="nav-button primary"
            onClick={handleSubmit}
            disabled={!isStepValid(4) || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div className="spinner" />
                Creating App...
              </>
            ) : (
              'Create App'
            )}
          </button>
        )}
      </div>

      {/* Decorative bottom pattern */}
      <div className="bottom-pattern">
        <div className="stripe" />
        <div className="stripe" />
        <div className="stripe" />
      </div>
    </div>
  );
};

export default CreateAppView;